export type BitmapLoader<T> = (url: string) => Promise<T>;

const DEFAULT_MAX_ENTRIES = 400;

export const loadImageBitmap: BitmapLoader<ImageBitmap> = async url => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Image ${url} failed with ${response.status}`);
  }
  return await createImageBitmap(await response.blob());
};

export class BitmapCache<T> {
  private readonly loaded = new Map<string, T>();
  private readonly pending = new Map<string, Promise<T>>();

  public constructor(
    private readonly loader: BitmapLoader<T>,
    private readonly maxEntries = DEFAULT_MAX_ENTRIES,
  ) {}

  public peek(url: string): T | null {
    const value = this.loaded.get(url);
    if (value === undefined) {
      return null;
    }
    this.loaded.delete(url);
    this.loaded.set(url, value);
    return value;
  }

  public has(url: string): boolean {
    return this.loaded.has(url);
  }

  public async get(url: string): Promise<T> {
    const existing = this.peek(url);
    if (existing !== null) {
      return existing;
    }
    const inFlight = this.pending.get(url);
    if (inFlight !== undefined) {
      return await inFlight;
    }
    const promise = this.loader(url)
      .then(value => {
        this.loaded.set(url, value);
        this.evict();
        return value;
      })
      .finally(() => {
        this.pending.delete(url);
      });
    this.pending.set(url, promise);
    return await promise;
  }

  public size(): number {
    return this.loaded.size;
  }

  private evict(): void {
    while (this.loaded.size > this.maxEntries) {
      const oldest = this.loaded.keys().next().value;
      if (oldest === undefined) {
        return;
      }
      const value = this.loaded.get(oldest);
      this.loaded.delete(oldest);
      if (typeof value === 'object' && value !== null && 'close' in value && typeof value.close === 'function') {
        value.close();
      }
    }
  }
}
